import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/use-auth";
import { Sentry } from "@/lib/sentry";

export type Milestones = Record<string, boolean>;

const LS_PREFIX = "caileve_milestone_";

// Legacy localStorage keys → milestone keys (for existing users).
const LEGACY_MAP: Record<string, string> = {
  privee_welcome_dismissed: "welcome_dismissed",
  privee_hint_home_clothing_suggest: "hint_clothing_1",
  privee_hint_home_builder_tray: "hint_builder_first",
  privee_hint_requests_tell_feel: "hint_request_first",
  privee_hint_jewelry_style_this_piece: "hint_jewelry_3",
  privee_hint_looks_emotional_profile: "hint_looks_3",
};

let cache: Milestones = {};
const listeners = new Set<(m: Milestones) => void>();

// In-flight deduplication — prevents concurrent useMilestones() callers
// from each spawning a separate network request on the same page load.
const fetchInflight = new Map<string, Promise<Milestones>>();

function emit(next: Milestones) {
  cache = next;
  listeners.forEach((l) => l(cache));
}

function readLocal(): Milestones {
  const out: Milestones = {};
  if (typeof window === "undefined") return out;
  try {
    for (const [legacy, key] of Object.entries(LEGACY_MAP)) {
      if (window.localStorage.getItem(legacy) === "1") out[key] = true;
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) {
        if (window.localStorage.getItem(k) === "1") {
          out[k.slice(LS_PREFIX.length)] = true;
        }
      }
    }
  } catch {
    /* ignore */
  }
  return out;
}

function writeLocal(key: string, value: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_PREFIX + key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// Safe Sentry wrapper — never let telemetry itself throw out of this module.
function safeCapture(
  e: unknown,
  context?: { tags?: Record<string, string>; extra?: Record<string, unknown> },
) {
  try {
    Sentry.captureException(e, context);
  } catch {
    /* ignore — telemetry must never propagate */
  }
}

// Safe emit — a misbehaving listener must not surface as an unhandled rejection.
function safeEmit(next: Milestones) {
  try {
    emit(next);
  } catch (e) {
    safeCapture(e, { tags: { source: "milestones-emit" } });
  }
}

// Isolated network reads/writes. Each returns a safe fallback on ANY failure
// (network error, Supabase error, thrown exception) and never rethrows.
async function fetchRemoteMilestones(
  userId: string,
): Promise<Milestones> {
  const existing = fetchInflight.get(userId);
  if (existing) return existing;
  const promise = (async (): Promise<Milestones> => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("onboarding_milestones")
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        safeCapture(new Error(error.message || "Network error"), {
          tags: { source: "milestones-fetch" },
        });
        return {};
      }
      return (
        ((data as { onboarding_milestones?: Milestones } | null)
          ?.onboarding_milestones as Milestones) ?? {}
      );
    } catch (e) {
      safeCapture(e, { tags: { source: "milestones-fetch" } });
      return {};
    } finally {
      fetchInflight.delete(userId);
    }
  })();
  fetchInflight.set(userId, promise);
  return promise;
}

async function writeRemoteMilestones(
  userId: string,
  next: Milestones,
  source: string,
  extra?: Record<string, unknown>,
): Promise<void> {
  try {
    const { error } = await supabase
      .from("profiles")
      .update({ onboarding_milestones: next })
      .eq("id", userId);
    if (error) {
      safeCapture(new Error(error.message || "Network error"), {
        tags: { source },
        extra,
      });
    }
  } catch (e) {
    safeCapture(e, { tags: { source }, extra });
  }
}

export function useMilestones() {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState<Milestones>(() => ({
    ...readLocal(),
    ...cache,
  }));
  const [loaded, setLoaded] = useState<boolean>(
    () => Object.keys(cache).length > 0,
  );

  useEffect(() => {
    const l = (m: Milestones) => setMilestones({ ...m });
    listeners.add(l);
    // Initialize with localStorage immediately for fast UI.
    if (Object.keys(cache).length === 0) safeEmit(readLocal());
    return () => {
      listeners.delete(l);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const local = readLocal();
      // Read remote milestones — guaranteed not to throw.
      const remote = await fetchRemoteMilestones(user.id);
      if (cancelled) return;

      // Merge: db is source of truth for true values, but local true also wins.
      const merged: Milestones = { ...local };
      try {
        for (const [k, v] of Object.entries(remote)) {
          if (v) merged[k] = true;
        }
      } catch (e) {
        safeCapture(e, { tags: { source: "milestones-merge" } });
      }
      safeEmit(merged);
      setLoaded(true);

      // Sync any local-only true keys up to the database — also guaranteed
      // not to throw.
      try {
        const toAdd: Milestones = {};
        let hasMissing = false;
        for (const [k, v] of Object.entries(local)) {
          if (v && !remote[k]) {
            toAdd[k] = true;
            hasMissing = true;
          }
        }
        if (hasMissing) {
          await writeRemoteMilestones(
            user.id,
            { ...remote, ...toAdd },
            "milestones-sync",
          );
        }
      } catch (e) {
        safeCapture(e, { tags: { source: "milestones-sync" } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const setMilestone = useCallback(
    (key: string, value = true) => {
      writeLocal(key, value);
      safeEmit({ ...cache, [key]: value });
      if (!user) return;
      (async () => {
        // Both calls are guaranteed not to throw.
        const remote = await fetchRemoteMilestones(user.id);
        const merged = { ...remote, [key]: value };
        await writeRemoteMilestones(user.id, merged, "milestones-write", {
          key,
        });
      })();
    },
    [user],
  );

  return { milestones, setMilestone, milestonesLoading: !loaded };
}